import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';

import { DocumentUploader } from '@shieldpay/bumblebee/document-uploader';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Column } from '@shieldpay/bumblebee/layout';
import DocumentUploaderMarkdown from '@shieldpay/bumblebee/markdown/document-uploader.md';
import { Text } from '@shieldpay/bumblebee/text';
import { TextList } from '@shieldpay/bumblebee/text-list';

import { noop } from '@shieldpay/utility-functions-ui/noop';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.documentUploader.title',
});

const sharedStaticProps = {
  uploadingText: 'Uploading',
  onChange: noop,
  onClear: noop,
  deleteDocumentText: 'Delete document',
};

const FieldSchema = z.object({
  file1: z.instanceof(File).refine((file) => {
    return file.size < 1 * 1024 * 1024;
  }, 'File needs to be under 1MB'),
});

export const DocumentUploaderComponentPage = () => {
  const [form, fields] = useForm({
    defaultNoValidate: false,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      return parseWithZod(formData, { schema: FieldSchema });
    },
  });

  const [mockState, setMockState] = useState<{
    state: 'idle' | 'uploading' | 'uploaded' | 'confirm-delete';
    name?: string;
  }>({ state: 'idle' });

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.documentUploader.title" />
      </Heading>

      <ContentsTable />

      <Column spacing="base">
        <Text paragraph>
          The document uploader is a UI-only field component for uploading
          files.
        </Text>
        <Text paragraph>
          It provides an input group combined with a List Item showing document
          info and should be used with the modal component for confirming delete
          DocumentUploader.Modal.
        </Text>
        <Text paragraph>
          To tie the logic together there is a hook `use-document-upload`
          designed to provide the custom logic to control these components.
        </Text>
      </Column>

      <ComponentPreview spacing="basePos5">
        <Form {...getFormProps(form)} spacing="base" method="POST">
          <DocumentUploader
            {...getInputProps(fields.file1, { type: 'file' })}
            errors={fields.file1.errors}
            label="Interactive example with help text"
            helpText="Try me"
            uploadingText="Uploading"
            onChange={() => {
              if (!fields.file1.valid) {
                setMockState({ state: 'idle', name: '' });
                return;
              }

              const name = fields.file1.value?.name;

              setMockState({
                state: 'uploading',
                name,
              });

              // simulate upload
              window.setTimeout(() => {
                setMockState({ state: 'uploaded', name });
              }, 2000);
            }}
            onClear={() => {
              setMockState(({ name }) => ({ state: 'confirm-delete', name }));
            }}
            validating={mockState.state === 'uploading'}
            value={mockState.name}
            deleteDocumentText="Delete document"
          >
            <FormattedMessage id="hive.components.documentUploader.uploadButton.label" />
          </DocumentUploader>
          <DocumentUploader.Modal
            cancelText="Cancel"
            closeText="Close"
            confirmText="Delete"
            title="Delete document"
            showModal={mockState.state === 'confirm-delete'}
            handleClearCancel={() => {
              setMockState((state) => ({ ...state, state: 'uploaded' }));
            }}
            handleClearConfirm={() => {
              setMockState({ state: 'idle' });

              form.reset({ name: fields.file1.name });
            }}
          />

          <DocumentUploader
            id="uploader2"
            name="uploader2"
            label="Static example - processing/uploading"
            validating
            value="uploading-file.jpg"
            {...sharedStaticProps}
          >
            <FormattedMessage id="hive.components.documentUploader.uploadButton.label" />
          </DocumentUploader>

          <DocumentUploader
            id="uploader3"
            name="uploader3"
            label="Static example - uploaded"
            value="uploaded-file.pdf"
            {...sharedStaticProps}
          >
            <FormattedMessage id="hive.components.documentUploader.uploadButton.label" />
          </DocumentUploader>
          <DocumentUploader
            id="uploader4"
            name="uploader4"
            label="Static example - validation message"
            errors={['Whoopsie']}
            aria-invalid
            aria-describedby="some-error-id"
            value={null}
            {...sharedStaticProps}
          >
            <FormattedMessage id="hive.components.documentUploader.uploadButton.label" />
          </DocumentUploader>
        </Form>
      </ComponentPreview>

      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            The document uploader is used to upload files from your computer or
            device by clicking the button to open the system’s file browser.
          </Text>
          <Text paragraph>
            It should be used to allow users to provide required documentation.
          </Text>
        </Design.Section>

        <Design.Section heading="Specs">
          <Text paragraph>The document uploader is made of: </Text>

          <Heading level="3" variant="bodyMedium150">
            Label
          </Heading>
          <TextList>
            <TextList.Item>Title of the required document.</TextList.Item>
            <TextList.Item>
              Should be short and concise [single line of text]
            </TextList.Item>
            <TextList.Item>
              Text style for the label is System Caption S
            </TextList.Item>
          </TextList>

          <Heading level="3" variant="bodyMedium150">
            Upload button
          </Heading>
          <TextList>
            <TextList.Item>
              Action to be performed [Upload + title of the document]{' '}
            </TextList.Item>
            <TextList.Item>Style: Secondary-button L </TextList.Item>
          </TextList>

          <Heading level="3" variant="bodyMedium150">
            Helper
          </Heading>
          <TextList>
            <TextList.Item>
              The helper text communicates to the user what file size or format
              limitations there are.
            </TextList.Item>
            <TextList.Item>
              Text style for the label is System Caption L
            </TextList.Item>
          </TextList>

          <Heading level="3" variant="bodyMedium150">
            Document
          </Heading>
          <TextList>
            <TextList.Item>
              The document should contain the name of the file that has been
              uploaded.
            </TextList.Item>
            <TextList.Item>
              The document has two states: uploading and uploaded. When the
              document has been uploaded the users will be able to remove it by
              clicking the “x” icon.
            </TextList.Item>
          </TextList>
          <Text paragraph variant="bodyMedium150">
            Please refer to Specs for examples
          </Text>
        </Design.Section>

        <Design.Section heading="Behaviour">
          <Heading level="3" variant="bodyMedium150">
            Default state
          </Heading>
          <TextList>
            <TextList.Item>
              Clicking the button will prompt the system’s file browser to open,
              allowing the user to select a document.
            </TextList.Item>
          </TextList>
          <Heading level="3" variant="bodyMedium150">
            Uploading state
          </Heading>
          <TextList>
            <TextList.Item>
              After the upload begins, the document component will populate
              below the file uploader button indicating the % indicating the
              percentage complete.
            </TextList.Item>
          </TextList>

          <Heading level="3" variant="bodyMedium150">
            Uploaded
          </Heading>
          <TextList>
            <TextList.Item>
              Once the document is uploaded, the user will be able to remove it
              using the “x” icon. Refer to icons for icon’s specs and touch
              areas.
            </TextList.Item>
            <TextList.Item>
              A modal will appear asking the user to confirm the action.
            </TextList.Item>
            <TextList.Item>
              A toast message will appear when the document is uploaded by the
              user.
            </TextList.Item>
          </TextList>
          <Text paragraph variant="bodyMedium150">
            Please refer to Behaviour for examples
          </Text>
        </Design.Section>

        <Design.Section heading="Placement">
          <Text paragraph>
            Please refer to the prototype for examples within a form
          </Text>
        </Design.Section>
        <Design.Section heading="Accessibility">
          <Text paragraph>
            Use the appropriate ARIA attributes for screen readers where
            applicable.
          </Text>
        </Design.Section>
      </Design>

      <MdxFormatter md={DocumentUploaderMarkdown} />
    </>
  );
};

export default DocumentUploaderComponentPage;
